import { Button } from "@components/ui/button"
import Traits from "../Traits/Traits"
import { Link } from "react-router-dom"
import { useAccountTitleButtonsSerializer } from "./useAccountTitleButtonsSerializer"
import { TriangleAlert } from "lucide-react"
import { useDrawer } from "@contexts/DrawerContext"
import { AccountNameForm } from "@components/AccountNew/AccountNameForm/AccountNameForm"
import { STATUS_COLOR_MAP } from "./utils"
import { Badge } from "@components/ui/badge"
import { renderSkeletonGroups } from "@utils/renderSkeletonGroups"

interface IAccountTitleProps {
  account_id: string
  classNames?: string
}

const AccountTitle = ({ account_id, classNames }: IAccountTitleProps) => {
  const { openDrawer } = useDrawer()
  const { data, isError, isPending, error } =
    useAccountTitleButtonsSerializer(account_id)

  if (isPending)
    return (
      <div
        className={`tw:flex tw:w-full tw:flex-col tw:gap-1 tw:text-xs ${classNames}`}
      >
        {renderSkeletonGroups([
          { count: 1, height: "tw:h-12" },
          { count: 3, height: "tw:h-5" },
        ])}
      </div>
    )

  if (isError) {
    return (
      <div className={`tw:w-full tw:text-red-500 ${classNames}`}>
        {`There was an error loading linked accounts. Error: ${error?.message}`}
      </div>
    )
  }

  const buttonElements = data.buttonItems.map((item, index) => {
    const {
      label,
      href,
      permissions,
      onClick,
      badge,
      icon,
      openNewWindow = true,
    } = item

    return (
      <Button
        key={`${label}-${index}`}
        variant="outline"
        size="sm"
        onClick={onClick}
        hidden={permissions}
      >
        {href ? (
          <Link
            to={href}
            target={openNewWindow ? "_blank" : "_self"}
            className="tw:flex tw:items-center tw:gap-1 tw:text-black tw:no-underline"
          >
            {label}
            {icon}
          </Link>
        ) : (
          <>
            {icon || label} {badge}
          </>
        )}
      </Button>
    )
  })

  const statusBackgroundColor = data.account_status_code
    ? STATUS_COLOR_MAP[data.account_status_code] || "tw:bg-gray-300"
    : "tw:bg-gray-300"

  return (
    <div className={`tw:flex tw:flex-col tw:gap-2 ${classNames}`}>
      <Button
        variant="link"
        className="tw:h-fit tw:w-fit tw:p-0 tw:text-foreground"
        size="lg"
        onClick={() =>
          openDrawer(
            <AccountNameForm account_id={account_id} />,
            "Update Profile Name",
          )
        }
      >
        <span className="tw:flex tw:flex-col tw:items-start">
          <span className="tw:text-4xl tw:text-wrap tw:text-left">{data.member_full_name}</span>
          {data.business_name && <span>{data.business_name}</span>}
        </span>
      </Button>

      <div hidden={!data.is_escalation} className="tw:text-sm tw:text-red-500">
        <TriangleAlert size={18} className="tw:m-1 tw:inline-block" />
        <span>This is an escalated member.</span>
      </div>

      <Badge
        className={`${statusBackgroundColor} tw:text-md tw:w-fit tw:text-foreground`}
      >
        {data.status_description}
      </Badge>

      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1">
        {buttonElements}
      </div>

      {data.prospect_guid && <Traits prospect_guid={data.prospect_guid} />}
    </div>
  )
}

export default AccountTitle
